import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { CoreJourneyService } from "./core-journey.service";
import { H01_LESSON_ID, H01HomeIntroductionService } from "./h01-home-introduction.service";
import { LearnOrientationLesson, LearnOrientationPrompt } from "./learn-orientation-lesson";
import { isOnHome } from "./onboarding-lesson-modal";

export const N01_LESSON_ID = "N01";

/** Recorded with N01's completed lesson state - N01's CompletedAt *is* this milestone. */
export const NOVA_LAUNCHER_INTRODUCED = "nova_launcher_introduced";

/** Anchors workbook: the Nova launcher on Home (the FAB / mobile tab). */
export const N01_ANCHOR = "nova.launcher";

/** Copy rows N01.01-N01.02, workbook v2.38 Copy sheet (verbatim). */
export const N01_COPY = {
  heading: "Meet Nova",
  body:
    "Nova is your flip* companion. Share what’s on your mind, explore what may be affecting your " +
    "capacity, or ask for help finding your way around flip*.",
};

/**
 * Onboarding lesson N01 "Meet Nova" (FLIP-388; spec v2.36 §24.1, workbook v2.38): an informational
 * anchored prompt at the Nova launcher on Home. Flow shared with the other Nova lessons - see
 * LearnOrientationLesson.
 *
 * - Eligible on the first Home visit after core onboarding is complete (the core-journey predicate;
 *   until then no prompt shows, and the next Home visit checks again) and the Home walkthrough (H01)
 *   has resolved: H01 completed or dismissed, or suppressed by Score history. While H01 is still
 *   open, N01 waits for it to close.
 * - Nova is never opened by the lesson. Completes on the visible close, or when Nova is opened
 *   while the prompt is visible.
 * - Nova opened independently (before the prompt ever showed) suppresses N01 permanently.
 * - Leaving Home - to complete a Score, say - closes the prompt and records nothing, so it comes
 *   back on the next Home visit.
 * - Not shown while Nova Companion is disabled.
 */
@Injectable({ providedIn: "root" })
export class N01MeetNovaService extends LearnOrientationLesson {
  protected readonly stepKey = N01_LESSON_ID;
  protected readonly milestone = NOVA_LAUNCHER_INTRODUCED;

  private companionEnabled = true;
  private suppressedThisSession = false;

  constructor(
    router: Router,
    coachMarkService: CoachMarkService,
    blockingUiSignal: BlockingUiSignalService,
    private readonly flags: FeatureFlagService,
    paymentsService: PaymentsService,
    private readonly steps: OnboardingStepService,
    interactionTracking: InteractionTrackingService,
    private readonly h01: H01HomeIntroductionService,
    companionSettings: NovaCompanionSettingsService,
    private readonly coreJourney: CoreJourneyService
  ) {
    super(router, coachMarkService, blockingUiSignal, flags, paymentsService, steps, interactionTracking);
    companionSettings.enabled$.subscribe((enabled) => (this.companionEnabled = enabled));
    paymentsService.subscriptionActive$.subscribe((active) => {
      if (!active) {
        this.suppressedThisSession = false;
      }
    });
  }

  /** Call on each Home arrival. Home has nothing to wait for beyond H01, so it is ready at once. */
  homeEntered(): void {
    this.enter();
    this.setReady(true);
  }

  /**
   * Call whenever Nova is opened (the launcher, the header badge). Completes N01 if its prompt is
   * on screen; otherwise Nova has been opened independently, so N01 is suppressed permanently.
   */
  onNovaOpened(): void {
    if (this.isShowing) {
      this.onTargetUsed("open_nova");
      return;
    }
    if (this.isCompleted || this.suppressedThisSession || !this.flags.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)) {
      return;
    }
    this.suppressedThisSession = true;
    this.steps.upsertStep({ stepKey: N01_LESSON_ID, dismissed: true });
  }

  /**
   * Core onboarding must be complete (a failed check holds back); then H01 must have resolved - if
   * it is open right now, wait for it to close.
   */
  protected eligible$(): Observable<boolean> {
    return this.coreJourney.status().pipe(
      take(1),
      switchMap((core) => (core?.complete ? this.h01Resolved$() : of(false)))
    );
  }

  private h01Resolved$(): Observable<boolean> {
    return forkJoin([this.steps.getStep(H01_LESSON_ID), this.steps.hasSubmittedScore()]).pipe(
      switchMap(([h01, hasScore]) => {
        if (h01 === undefined || hasScore === null) {
          return of(false); // couldn't check - hold back rather than guess
        }
        const resolved = !!h01?.completedAt || !!h01?.dismissed || hasScore || this.h01.completedThisSession;
        return resolved ? of(true) : this.h01.completed$.pipe(take(1), map(() => true));
      })
    );
  }

  protected prompt(): LearnOrientationPrompt | null {
    if (!this.companionEnabled) {
      return null;
    }
    return { lessonId: N01_LESSON_ID, target: N01_ANCHOR, ...N01_COPY };
  }

  protected isOnSurface(): boolean {
    return isOnHome(this.router);
  }
}
